import * as members from '../../privates/privateMembers';
import {validate} from '../../privates/validations/validation';
import {emptyStringIfNil} from '../../privates/utils';

/**
 * @mixin LabelMixin
 * @summary Provides functionality for elements that have a label.
 * @description
 *  `LabelMixin` is a mixin that provides basic properties for elements on the page that have a label.
 * 
 *  **About Mixins**
 * 
 *  [Mixins](https://support.wix.com/en/article/velo-about-mixins) provide functionality that other elements can inherit and use. 
 *  
 *  Mixins are not elements. You cannot add mixins to a page in the Editor like other `$w` elements, and mixins are not meant to be used directly 
 *  in your code. 
 *  
 *  For example, you would not write code like this, because it is out of context: 
 * 
 *  ```javascript
 *  let label = $w("#LabelMixin").label;
 *  ```
 * 
 *  Instead, you can code the following if `myElement` is an element that 
 *  "mixes in" `LabelMixin`. 
 * 
 *   ```javascript
 *   let label = $w("#myElement").label;
 *   ```
 *  
 * @memberof $w
 */
class LabelMixin {
    /**
     * @member label
     * @label property
     * @syntax
     * get label(): string
     * set label(value: string): void
     * @summary Sets or gets the label of an element.
     * @snippet [LabelMixin-label_get.es6=Get an element's label]
     * @snippet [LabelMixin-label_set.es6=Set an element's label]
     * @type {external:String}
     * @default Empty string
     * @memberof $w.LabelMixin
     * @instance
     */
    get label() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).label;
    }

  /**
   * @member label
   * @memberof $w.LabelMixin
   * @param {external:String} value
   */
    set label(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'label',
            dataToValidate: {label: value},
            compName: this.id
        });
        if (!isValid) {
            return;
        }
        const label = emptyStringIfNil(value);
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setData(compId, {label});
    }

}

export default LabelMixin;
